import React, { useState } from "react";
import {
  ColumnDef,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import {
  displayDate,
  displayValue,
  formatToPKR,
  downloadCSV,
  srbReportMapper,
  downloadPDF,
  capitalizeFirstLetter,
  replaceUnderscoreWithSpace,
  formatDate,
} from "@/lib/helper";
import Pagination from "@/components/ui/table/pagination-table";
import { DataTableColumnHeader } from "@/components/ui/table/data-table-column-header";
import { useGetTransactionTaxesReportQuery, useLazyGetTransactionTaxesReportForExportQuery } from "@/services/transactionApi";
import { DonationReport } from "@/types/pos/donation-report";
import { SRBReport } from "@/types/pos/srb-report";
import { Separator } from "@/components/ui/separator";
import { DataTableViewOptions } from "./data-table-view-options";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import TableSearchFilter from "@/components/ui/table/data-table-search";
import { DatePickerWithRange } from "@/components/ui/date-range/date-rangePicker";
import DonationSummary from "../summary/donation-summary";
import TableFilters from "@/components/ui/table/data-table-filter";

export default function DonationReportTable() {
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;

  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Filter state
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [pendingFilters, setPendingFilters] = useState<Record<string, any>>({});
  const [openFilter, setOpenFilter] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Build query for table with server-side pagination
  const buildTableQuery = () => {
    const params = new URLSearchParams();
    params.append("limit", String(pageSize));
    params.append("offset", String(currentPage * pageSize));
    params.append("sort_order", "desc");
    params.append("sort_key", "id");
    params.append("tax_type", "donation"); // Specific for this report
    params.append("load_summary", "true"); // Fetch summary tiles

    // Add search term if present
    if (searchTerm) {
      params.append("search_key", searchTerm);
    }

    // Add applied filters to query
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        if (Array.isArray(value)) {
          value.forEach((val) => {
            if (val) {
              params.append(key, String(val));
            }
          });
        } else {
          params.append(key, String(value));
        }
      }
    });

    return params.toString();
  };

  // API call for table data
  const { data: tableApiData, isLoading } = useGetTransactionTaxesReportQuery(
    { query: buildTableQuery(), org_id: orgId },
    { skip: !orgId }
  );

  const tableData = React.useMemo(() => {
    return (Array.isArray(tableApiData?.data) ? tableApiData?.data : []) as DonationReport[];
  }, [tableApiData]);

  // Reset to first page when filters or search changes
  React.useEffect(() => {
    setCurrentPage(0);
  }, [filters, searchTerm]);

  const toggleSortOrder = (key: string, order: string) => {
    setSorting([{ id: key, desc: order === 'desc' }]);
  };

  const searchCriteria = {
    sort_key: sorting[0]?.id || 'id',
    sort_order: sorting[0]?.desc ? 'desc' : 'asc'
  };

  const filterDisplay = React.useMemo(() => [
    {
      type: "select",
      name: "tax_submission_type",
      label: "Submission Type",
      options: [
        { id: "Sale", name: "Sale" },
        { id: "Refund", name: "Returned" },
      ],
      function: (value: string) => {
        setPendingFilters(prev => ({
          ...prev,
          tax_submission_type: value,
        }));
      },
    },
  ], []);

  // Helper function to build export query (all data without pagination)
  const buildExportQuery = () => {
    const exportParams = new URLSearchParams();
    exportParams.append("offset", "0");
    exportParams.append("sort_order", "desc");
    exportParams.append("sort_key", "id");
    exportParams.append("tax_type", "donation"); // Specific for this report
    exportParams.append("load_summary", "true"); // Fetch summary tiles

    // Add search term if present
    if (searchTerm) {
      exportParams.append("search_key", searchTerm);
    }

    // Add applied filters to query
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        if (Array.isArray(value)) {
          value.forEach((val) => {
            if (val) {
              exportParams.append(key, String(val));
            }
          });
        } else {
          exportParams.append(key, String(value));
        }
      }
    });

    return exportParams.toString();
  };

  const { toast } = useToast();
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [selectAllRows, setSelectAllRows] = useState(false);
  const [showSelectAllNotification, setShowSelectAllNotification] = useState(false);
  const [getTransactionTaxesReportForExport] = useLazyGetTransactionTaxesReportForExportQuery();

  const columns: ColumnDef<DonationReport>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value: any) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
          className="translate-y-[2px]"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value: any) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="translate-y-[2px]"
        />
      ),
    },
    {
      accessorKey: "receipt_number",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Txn Number" sortKey="id" searchCriteria={searchCriteria} toggleSortOrder={toggleSortOrder} />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex gap-2 items-center justify-between w-fit">
            <button
              onClick={row.getToggleExpandedHandler()}
              className="flex gap-1 items-center"
            >
              {row.getIsExpanded() ? (
                <i className="fa fa-angle-down w-3 h-3"></i>
              ) : (
                <i className="fa fa-angle-right w-3 h-3"></i>
              )}
            </button>
            <div className="">
              <p className="capitalize cursor-pointer">
                <span>{displayValue(row.original.receipt_number)}</span>
              </p>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "transaction_receipt_number",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Transaction Receipt Number" sortKey="transaction_receipt_number" searchCriteria={searchCriteria} toggleSortOrder={toggleSortOrder} />
      ),
      cell: ({ row }) => displayValue(row.original.transaction_receipt_number),
    },
    {
      accessorKey: "created_at",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Date" sortKey="created_at" searchCriteria={searchCriteria} toggleSortOrder={toggleSortOrder} />
      ),
      cell: ({ row }) => displayDate(row.original.created_at),
    },
    {
      accessorKey: "sub_total",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Net Price" sortKey="sub_total" sortable={false} searchCriteria={searchCriteria} toggleSortOrder={toggleSortOrder} />
      ),
      cell: ({ row }) => formatToPKR(row.original.sub_total),
    },
    {
      accessorKey: "discount",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Discount" sortKey="discount" sortable={false} searchCriteria={searchCriteria} toggleSortOrder={toggleSortOrder} />
      ),
      cell: ({ row }) => formatToPKR(row.original.discount),
    },
    {
      id: "subtotal_after_discount",
      header: "Subtotal",
      cell: ({ row }) => formatToPKR(Math.max(0, Number(row.original.sub_total || 0) - Number(row.original.discount || 0))),
    },
    {
      accessorKey: "tax_rate",
      header: "Tax Rate (%)",
      cell: ({ row }) => row.original.tax_rate === -1 ? "N/A" : displayValue(row.original.tax_rate),
    },
    {
      accessorKey: "tax_amount",
      header: "Tax Amount",
      cell: ({ row }) => formatToPKR(row.original.tax_amount),
    },
    {
      accessorKey: "total",
      header: "Total Amount",
      cell: ({ row }) => formatToPKR(row.original.total),
    },
    {
      accessorKey: "tax_submission_type",
      header: "Submission Type",
      cell: ({ row }) => {
        const type = row.original.tax_submission_type;

        const isRefund = type === "Refund";
        const label = type === "Refund" ? "Returned" : "Sale";

        return (
          <span
            className={`px-3 py-1 text-xs font-medium rounded-full ${
              isRefund
                ? "bg-red-100 text-red-600"
                : "bg-green-100 text-green-600"
            }`}
          >
            {label}
          </span>
        );
      },
    },
    {
      accessorKey: "tax_type",
      header: "Tax Type",
      cell: ({ row }) => <span className="capitalize">{displayValue(row.original.tax_type)}</span>,
    },
  ];

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: () => true,
  });

  const handleExportCSV = async () => {
    if (selectAllRows) {
      try {
        const result = await getTransactionTaxesReportForExport({ query: buildExportQuery(), org_id: orgId }).unwrap();

        if (result?.data && result.data.length > 0) {
          downloadCSV(result.data as SRBReport[], "all_donation_report.csv", srbReportMapper);
          toast({ variant: "success", title: "Exported successfully" });
        } else {
          toast({ variant: "destructive", title: "No data to export" });
        }
      } catch (error) {
        toast({ variant: "destructive", title: "Export failed" });
      }
    } else {
      const selectedRows = table.getSelectedRowModel().rows.map((row) => row.original);
      if (selectedRows.length === 0) {
        toast({ variant: "destructive", title: "Please select one or more records" });
        return;
      }
      downloadCSV(selectedRows as SRBReport[], "donation_report.csv", srbReportMapper);
    }
  };

  const handleExportPDF = async (orientation: 'portrait' | 'landscape' = 'portrait') => {
    if (selectAllRows) {
      try {
        const result = await getTransactionTaxesReportForExport({ query: buildExportQuery(), org_id: orgId }).unwrap();
        if (result?.data && result.data.length > 0) {
          await downloadPDF(
            {
              ...result,
              data: result.data.map(item => ({
                ...item,
                id: item.transaction_id,
              })) as any
            },
            "all_donation_report.pdf",
            "Donation Tax Report",
            {
              start_date: filters.start_date,
              end_date: filters.end_date
            },
            orientation,
            "tax-report"
          );
          toast({ variant: "success", title: "PDF Exported successfully" });
        } else {
          toast({ variant: "destructive", title: "No data to export" });
        }
      } catch (error) {
        toast({ variant: "destructive", title: "PDF Export failed" });
      }
    } else {
      const selectedRows = table.getSelectedRowModel().rows.map((row) => row.original);
      if (selectedRows.length === 0) {
        toast({ variant: "destructive", title: "Please select one or more records" });
        return;
      }

      await downloadPDF(
        {
          data: selectedRows.map(item => ({
            ...item,
            id: item.transaction_id,
          })) as any,
          total_counts: tableApiData?.total_counts,
          filtered_counts: selectedRows.length,
        } as unknown as any,
        `selected_donation_report.pdf`,
        "Donation Tax Report - Selected Records",
        {
          start_date: filters.start_date,
          end_date: filters.end_date,
        },
        orientation,
        "tax-report"
      );
      toast({
        variant: "success",
        title: `PDF exported successfully`,
      });
    }
  };

  const handleSelectAllRows = () => {
    setSelectAllRows(true);
    setShowSelectAllNotification(false);
  };

  const handleClearSelection = () => {
    setSelectAllRows(false);
    setShowSelectAllNotification(false);
    table.toggleAllPageRowsSelected(false);
    setRowSelection({});
  };

  const handleDateRange = (dates: {
    start_date: Date | undefined;
    end_date: Date | undefined;
  }) => {
    const formattedStartDate = dates.start_date ? formatDate(dates.start_date) : "";
    const formattedEndDate = dates.end_date ? formatDate(dates.end_date) : "";

    const newFilters = {
      ...filters,
      start_date: formattedStartDate || undefined,
      end_date: formattedEndDate || undefined,
    };
    
    setPendingFilters(newFilters);
    setFilters(newFilters);
  };

  const applyFilters = () => {
    setFilters(pendingFilters);
    setOpenFilter(false);
  };

  const resetFilters = () => {
    setPendingFilters({});
    setFilters({});
    setSearchTerm("");
    setCurrentPage(0);
  };

  // Handle showing select all notification
  React.useEffect(() => {
    const allPageRowsSelected = table.getIsAllPageRowsSelected();
    const selectedRowCount = table.getSelectedRowModel().rows.length;
    const totalRowsInCurrentPage = table.getRowModel().rows.length;

    if (allPageRowsSelected && selectedRowCount === totalRowsInCurrentPage && totalRowsInCurrentPage > 0 && !selectAllRows) {
      setShowSelectAllNotification(true);
    } else if (selectedRowCount === 0 || selectAllRows) {
      setShowSelectAllNotification(false);
    }
  }, [table.getIsAllPageRowsSelected(), table.getSelectedRowModel().rows.length, selectAllRows]);

  // Reset select all when row selection changes
  React.useEffect(() => {
    if (Object.keys(rowSelection).length === 0) {
      setSelectAllRows(false);
      setShowSelectAllNotification(false);
    }
  }, [rowSelection]);

  const totalRecords = tableApiData?.filtered_counts || 0;

  const handleLimitChange = (newLimit: number) => {
    setPageSize(newLimit);
    setCurrentPage(0);
  };

  const handleNextPage = () => {
    if (currentPage < Math.ceil(totalRecords / pageSize) - 1) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleFirstPage = () => {
    setCurrentPage(0);
  };

  const handleLastPage = () => {
    setCurrentPage(Math.ceil(totalRecords / pageSize) - 1);
  };

  const isLastPage = currentPage >= Math.ceil(totalRecords / pageSize) - 1;

  return (
    <div className="space-y-4">
      <DonationSummary data={tableApiData} isLoading={isLoading} />
      <Card>
        <CardHeader className="flex flex-col space-y-4 pb-4">
          <div className="flex flex-col gap-4 w-full">
            <div className="flex flex-col sm:flex-row sm:flex-wrap lg:flex-nowrap gap-2 w-full items-center">
              {/* Search - full width on mobile, auto on larger */}
              <div className="w-full sm:flex-1 sm:min-w-0">
                <TableSearchFilter
                  setSearchCriteria={() => {}}
                  inputValue={searchTerm}
                  setInputValue={setSearchTerm}
                  placeHolder="Search by Txn Number"
                />
              </div>

              {/* DatePicker + Buttons in one row on mobile too */}
              <div className="flex flex-row gap-2 w-full sm:w-auto sm:flex-shrink-0 items-center">
                <div className="flex-1 sm:flex-none sm:w-48 lg:w-56">
                  <DatePickerWithRange
                    name={"Date Range"}
                    value={{
                      start_date: pendingFilters.start_date ? new Date(pendingFilters.start_date) : undefined,
                      end_date: pendingFilters.end_date ? new Date(pendingFilters.end_date) : undefined,
                    }}
                    onValueChange={handleDateRange}
                    label={"Select date range"}
                    className="w-full h-8"
                  />
                </div>

                <Button
                  variant={"default"}
                  className="bg-primary text-black flex items-center justify-center h-8 w-8 p-0 sm:w-auto sm:px-3 flex-shrink-0"
                  onClick={applyFilters}
                >
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex justify-center items-center gap-2">
                          <i className="fa fa-filter text-xs sm:text-sm"></i>
                          <span className="hidden sm:inline text-sm">Apply</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Apply Filters</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Button>

                <Button
                  variant={"outline"}
                  className="text-black flex items-center justify-center h-8 w-8 p-0 sm:w-auto sm:px-3 flex-shrink-0"
                  onClick={resetFilters}
                >
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex justify-center items-center gap-2">
                          <i className="fa-solid fa-arrows-rotate text-xs sm:text-sm"></i>
                          <span className="hidden sm:inline text-sm">Reset</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Reset Filter</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Button>

                <DataTableViewOptions
                  table={table}
                  onExportCSV={handleExportCSV}
                  onExportPDF={handleExportPDF}
                />
                <button
                  className={`border bg-white rounded-full size-3 text-gray-400 p-4 flex items-center justify-center ${Object.keys(filters).length ? "border-primary" : ""}`}
                  onClick={() => setOpenFilter(true)}
                >
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <i
                          className={`fa fa-filter ${Object.keys(filters).length ? "text-primary" : ""}`}
                        ></i>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>click to apply filter</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </button>
              </div>
            </div>
          </div>
        </CardHeader>

        <TableFilters
          isOpen={openFilter}
          setOpen={setOpenFilter}
          initialValue={{
            sort_key: "id",
            sort_order: "desc",
          }}
          filterData={pendingFilters}
          setFilter={setPendingFilters}
          setSearchCriteria={(value: any) => {
            const updatedFilters = typeof value === "function" ? value(pendingFilters) : value;
            const nextFilters = {
              ...(updatedFilters.start_date ? { start_date: updatedFilters.start_date } : {}),
              ...(updatedFilters.end_date ? { end_date: updatedFilters.end_date } : {}),
              ...(updatedFilters.tax_submission_type ? { tax_submission_type: updatedFilters.tax_submission_type } : {}),
            };
            setPendingFilters(nextFilters);
            setFilters(nextFilters);
            setCurrentPage(0);
          }}
          filterDisplay={filterDisplay}
          setInputValue={setSearchTerm}
          onReset={resetFilters}
        />

        {/* Gmail-style Select All Notification */}
        {(showSelectAllNotification || selectAllRows) && (
          <div className="bg-blue-50 border-y border-blue-100 p-2 px-3 sm:px-6 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0 justify-between">
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
              <div className="text-xs sm:text-sm text-blue-800">
                {selectAllRows ? (
                  <>
                    All <strong>{tableApiData?.filtered_counts || 0}</strong> transactions are selected.
                  </>
                ) : (
                  <>
                    All <strong>{table.getSelectedRowModel().rows.length}</strong> transactions on this page are selected.
                  </>
                )}
              </div>
              {!selectAllRows && (
                <button
                  onClick={handleSelectAllRows}
                  className="text-blue-600 hover:text-blue-800 underline text-xs sm:text-sm font-medium sm:ml-2"
                >
                  Select all {tableApiData?.filtered_counts || 0} transactions
                </button>
              )}
              {selectAllRows && (
                <button
                  onClick={handleClearSelection}
                  className="text-blue-600 hover:text-blue-800 underline text-xs sm:text-sm font-medium sm:ml-2"
                >
                  Clear selection
                </button>
              )}
            </div>
          </div>
        )}
        <CardContent>
          <ScrollArea className="h-[calc(100vh-450px)] sm:h-[calc(100vh-400px)] md:h-[calc(100vh-350px)] rounded-md border">
            <Table>
              <TableHeader className="bg-secondary">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="h-24 text-center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : tableData.length > 0 ? (
                  table.getRowModel().rows.map((row) => (
                    <React.Fragment key={row.id}>
                      <TableRow data-state={row.getIsSelected() && "selected"}>
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                      {row.getIsExpanded() && (
                        <TableRow>
                          <TableCell colSpan={columns.length} className="bg-muted/50 p-2 sm:p-4">
                            <div className="space-y-3 sm:space-y-4">
                              <h3 className="text-base sm:text-lg font-bold">Transaction Details</h3>
                              
                              {/* Desktop View - Hidden on mobile */}
                              <div className="hidden md:block">
                                <div className="grid grid-cols-12 py-2 gap-4 font-semibold border-b text-sm">
                                  <span className="col-span-3 text-left">Item Name</span>
                                  <span className="col-span-2 text-left">Type</span>
                                  <span className="col-span-1 text-left">Qty</span>
                                  <span className="col-span-2 text-left">Unit Price</span>
                                  <span className="col-span-1 text-left">Discount</span>
                                  <span className="col-span-1 text-left">Tax</span>
                                  <span className="col-span-2 text-right">Total</span>
                                </div>

                                {row.original.items?.map((item, i) => (
                                  <div key={i} className="grid grid-cols-12 py-2 gap-4 border-b last:border-b-0 items-center text-sm">
                                    <span className="capitalize col-span-3 text-left">
                                      {item.description}
                                    </span>
                                    <span className="col-span-2 text-left">
                                      {capitalizeFirstLetter(replaceUnderscoreWithSpace(item.item_type))}
                                    </span>
                                    <span className="col-span-1 text-left">{item.quantity}</span>
                                    <span className="col-span-2 text-left">{formatToPKR(item.price)}</span>
                                    <span className="col-span-1 text-left">{formatToPKR(item.discount)}</span>
                                    <span className="col-span-1 text-left">{formatToPKR(item.tax_amount)}</span>
                                    <span className="col-span-2 text-right">{formatToPKR(item.total)}</span>
                                  </div>
                                ))}
                              </div>

                              {/* Mobile View - Card-based layout */}
                              <div className="md:hidden space-y-3">
                                {row.original.items?.map((item, i) => (
                                  <div key={i} className="border rounded-lg p-3 bg-white space-y-2 text-sm">
                                    <div className="font-semibold capitalize border-b pb-2">
                                      {item.description}
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                      <div>
                                        <span className="text-gray-500 text-xs">Type:</span>
                                        <div className="font-medium">
                                          {capitalizeFirstLetter(replaceUnderscoreWithSpace(item.item_type))}
                                        </div>
                                      </div>
                                      <div>
                                        <span className="text-gray-500 text-xs">Qty:</span>
                                        <div className="font-medium">{item.quantity}</div>
                                      </div>
                                      <div>
                                        <span className="text-gray-500 text-xs">Unit Price:</span>
                                        <div className="font-medium">{formatToPKR(item.price)}</div>
                                      </div>
                                      <div>
                                        <span className="text-gray-500 text-xs">Discount:</span>
                                        <div className="font-medium">{formatToPKR(item.discount)}</div>
                                      </div>
                                      <div>
                                        <span className="text-gray-500 text-xs">Tax:</span>
                                        <div className="font-medium">{formatToPKR(item.tax_amount)}</div>
                                      </div>
                                      <div className="col-span-2">
                                        <span className="text-gray-500 text-xs">Total:</span>
                                        <div className="font-bold text-base">{formatToPKR(item.total)}</div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>

                              <Separator className="my-4" />
                              
                              <div className="grid grid-cols-1 gap-4 sm:gap-8">
                                <div className="space-y-1 w-full max-w-md ml-auto text-sm sm:text-base">
                                  <div className="flex justify-between">
                                    <span>Net Price</span>
                                    <span>{formatToPKR(row.original.sub_total)}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span>Discount</span>
                                    <span>-{formatToPKR(row.original.discount)}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span>Tax Amount</span>
                                    <span>{formatToPKR(row.original.tax_amount)}</span>
                                  </div>
                                  <div className="flex justify-between font-bold text-base sm:text-lg border-t pt-2">
                                    <span>Total Amount</span>
                                    <span>{formatToPKR(row.original.total)}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="h-24 text-center">
                      No results found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
          <div className="pt-4">
            <Pagination
              limit={pageSize}
              offset={currentPage * pageSize}
              totalItems={totalRecords}
              onLimitChange={handleLimitChange}
              onNextPage={handleNextPage}
              onPrevPage={handlePrevPage}
              onFirstPage={handleFirstPage}
              onLastPage={handleLastPage}
              isLastPage={isLastPage}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
